import { parseUrlToIndexUiState, pathnameIsJobsListingPath } from '../search-router/url-handling';

/**
 * Removes `returnTo` from a saved-search URL and returns a stable string for storage and deduping.
 * Accepts absolute or relative URLs; invalid / missing input yields `''`.
 */
export function normalizeSavedSearchUrlInput(input: string): string {
  if (input == null || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.jobcrawls.com';
    const u = new URL(trimmed, base);
    u.searchParams.delete('returnTo');
    if (u.searchParams.toString() === '') {
      u.search = '';
    }
    return `${u.origin}${u.pathname}${u.search}`;
  } catch {
    return trimmed.startsWith('/') ? trimmed.split('#')[0] : `/${trimmed}`.split('#')[0];
  }
}

function pathAndSearchFromUrl(input: string): string {
  if (input == null || typeof input !== 'string') return '/';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.jobcrawls.com';
    const u = new URL(input, base);
    return `${u.pathname}${u.search}`;
  } catch {
    const raw = String(input);
    return raw.startsWith('/') ? raw.split('#')[0] : `/${raw}`.split('#')[0];
  }
}

/**
 * URL params that describe how results are presented, not what is being searched for. They are
 * dropped from the stored form of a search (`stripEphemeralSearchParams`) and ignored by
 * `searchIdentityKey`, so paging through results, switching to the map, or changing the page size
 * never creates a "new" saved search.
 */
const PRESENTATION_PARAMS = ['page', 'perPage', 'view'] as const;

/**
 * Filter keys that only narrow by recency. They never make a URL count as a search on their own
 * (`/en/jobs?firstSeenMin=24h` is "what's new", not something worth keeping or alerting on), but
 * they are kept when they accompany real content.
 */
const RECENCY_ONLY_REFINEMENTS = new Set(['firstSeenMin', 'lastSeenMin']);

/** A numeric filter at zero (`salaryRangeMin=0`, a slider dragged all the way down) is no filter. */
function isZeroValue(value: string): boolean {
  return /^0+(\.0+)?$/.test(value.trim());
}

/** `firstSeenMin`/`lastSeenMin` carry either a relative window ("24h") or an absolute epoch-ms. */
function isAbsoluteEpochValue(value: string): boolean {
  return /^\d{11,}$/.test(value.trim());
}

/**
 * Drops presentation-only params (`page`, `perPage`, `view`) and absolute-timestamp recency
 * filters (`firstSeenMin=1784614220176`, the "since my last visit" form, which is only meaningful
 * for the session that wrote it) from a search URL. Accepts absolute or relative input and returns
 * the same shape it was given. Not applied inside `normalizeSavedSearchUrlInput` on purpose:
 * that function feeds `savedSearchAlertId`, and rewriting it would orphan the account items of
 * anyone who already enabled an alert on a URL carrying one of these params.
 */
export function stripEphemeralSearchParams(input: string): string {
  if (input == null || typeof input !== 'string' || !input.trim()) return '';
  const trimmed = input.trim();
  const isAbsolute = /^https?:\/\//i.test(trimmed);
  try {
    const u = new URL(trimmed, 'https://www.jobcrawls.com');
    for (const key of PRESENTATION_PARAMS) u.searchParams.delete(key);
    for (const key of RECENCY_ONLY_REFINEMENTS) {
      const values = u.searchParams.getAll(key);
      if (values.length === 0) continue;
      u.searchParams.delete(key);
      for (const value of values) {
        if (!isAbsoluteEpochValue(value)) u.searchParams.append(key, value);
      }
    }
    if (u.searchParams.toString() === '') u.search = '';
    return isAbsolute ? `${u.origin}${u.pathname}${u.search}` : `${u.pathname}${u.search}`;
  } catch {
    return trimmed;
  }
}

/**
 * The stored form of a recorded search: `normalizeSavedSearchUrlInput` plus
 * `stripEphemeralSearchParams`. Every write into the saved-searches list goes through this so
 * that deliberate saves (alert CTA, suggestion accept) and passive recording agree on the URL,
 * and therefore on the account item id.
 */
export function toStoredSearchUrl(input: string): string {
  const normalized = normalizeSavedSearchUrlInput(input);
  return normalized ? stripEphemeralSearchParams(normalized) : '';
}

/**
 * True when a URL is not worth recording as a search. A URL counts as a search only when it is a
 * jobs-listing route (never an employer profile such as `/en/company/x`, which the global
 * SearchProvider also parses into a company filter) AND, after dropping presentation params, it
 * carries real content: a query of at least two characters, or at least one refinement other than
 * a recency-only one. Sort order, page, page size, map view, global scope (`finland=0`) and
 * "new in the last 24h" are modifiers, not searches, so on their own they are skipped.
 */
export function shouldSkipPersistingSavedSearchUrl(input: string): boolean {
  if (input == null || typeof input !== 'string' || !input.trim()) return true;
  const normalized = normalizeSavedSearchUrlInput(input);
  const pathAndSearch = stripEphemeralSearchParams(pathAndSearchFromUrl(normalized));
  if (!pathnameIsJobsListingPath(pathAndSearch)) return true;
  const state = parseUrlToIndexUiState(pathAndSearch);

  const q = state.query?.trim() ?? '';
  const hasQuery = q.length >= MIN_SEARCH_QUERY_CHARS;
  // A query below the minimum is a keystroke state whatever filters sit next to it
  // ("search=h&jobType=full-time" is not a search for "h" jobs).
  if (q.length > 0 && !hasQuery) return true;

  const refinements = state.refinementList ?? {};
  const hasContentRefinement = Object.keys(refinements).some((key) => {
    if (RECENCY_ONLY_REFINEMENTS.has(key)) return false;
    const v = refinements[key];
    if (!Array.isArray(v) || v.length === 0) return false;
    return v.some((x) => x != null && String(x).trim() !== '' && !isZeroValue(String(x)));
  });

  return !hasQuery && !hasContentRefinement;
}

/** A one-character query is a keystroke, not a search. */
export const MIN_SEARCH_QUERY_CHARS = 2;

export function isRecencyOnlyRefinement(key: string): boolean {
  return RECENCY_ONLY_REFINEMENTS.has(key);
}
